'use client';

import { useContainerVisibility } from '@/hooks/useContainerVisibility';
import { ENABLE_VIDEO_OVERLAY } from '@/util/constants';
import {
  ArrowPathIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from '@heroicons/react/24/outline';
import { usePlayback } from '@vimond/features/video-playback';
import {
  AssetType,
  CuratedAssetType,
} from '@vimond/services/content-discovery';
import { getEnvVar } from '@vimond/utils/env';
import dynamic from 'next/dynamic';
import { Suspense, useEffect, useState } from 'react';

const VideoJSPlayerBackground = dynamic(
  () =>
    import('./player/VideoJSPlayerPreview').then(
      (mod) => mod.VideoJSPlayerPreview,
    ),
  { ssr: false },
);

type Props = {
  asset: CuratedAssetType | AssetType;
  heroItem?: boolean;
};

const LOCAL_STORAGE_PREVIEW_MUTED_KEY = 'preview-muted';

export const VideoBackdrop = ({
  asset: assetItem,
  heroItem = false,
}: Props & React.ComponentPropsWithoutRef<'section'>) => {
  // check if asset is of type CuratedAssetType or AssetType
  const asset =
    (assetItem as CuratedAssetType).content !== undefined
      ? (assetItem as CuratedAssetType).content!
      : (assetItem as AssetType);

  // is the player muted or finished playing
  const [isPlayerMuted, setIsPlayerMuted] = useState(false);
  const [isPlayerFinishedPlaying, setIsPlayerFinishedPlaying] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);

  // get playback object from useVideoPlayback hook
  const { playback } = usePlayback(
    asset.id,
    typeof window !== 'undefined' ? window.navigator.userAgent : '',
  );

  // get preview marker from asset if it exists
  const videoPreviewMarker = asset.markers?.find(
    (marker) => marker.type === getEnvVar('NEXT_PUBLIC_PREVIEW_MARKER_TYPE'),
  );

  useEffect(() => {
    // get player muted state from local storage as bool value
    const userPreviewMutedValue = localStorage.getItem(
      LOCAL_STORAGE_PREVIEW_MUTED_KEY,
    );
    setIsPlayerMuted(
      userPreviewMutedValue === null || userPreviewMutedValue === 'true',
    );
  }, []);

  const { containerRef, isContainerVisible } = useContainerVisibility();

  const missingPreviewPlayback = !playback || !videoPreviewMarker;
  if (missingPreviewPlayback) return <></>;
  if (!isContainerVisible)
    return (
      <div className="absolute top-0 left-0 w-full h-full" ref={containerRef} />
    );

  const storeMutedState = (muted: boolean) => {
    // store muted state in local storage
    localStorage.setItem(LOCAL_STORAGE_PREVIEW_MUTED_KEY, muted.toString());
    setIsPlayerMuted(muted);
  };

  return (
    <>
      <div className="absolute top-0 left-0 w-full h-full">
        <Suspense fallback={<></>}>
          <VideoJSPlayerBackground
            key={playback.assetId} // important to not reuse the same player
            playback={{
              ...playback,
              startTime: videoPreviewMarker?.startTime ?? playback.startTime,
              endTime: videoPreviewMarker?.endTime ?? playback.endTime,
            }}
            forceLowQuality={!heroItem}
            finished={isPlayerFinishedPlaying}
            muted={isPlayerMuted}
            onPlaybackFinished={() => setIsPlayerFinishedPlaying(true)}
            onPlaybackReady={() => setIsPlayerReady(true)}
            onMutedChange={(muted: boolean) => setIsPlayerMuted(muted)}
          />
        </Suspense>
        {ENABLE_VIDEO_OVERLAY && (
          <div className="absolute bottom-0 left-0 right-0 pointer-events-none h-1/3 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        )}
      </div>
      {isPlayerReady && (
        <div className="absolute right-0 top-16 z-50 flex gap-4 bg-black bg-opacity-30 p-5 text-white lg:bottom-16 lg:top-[inherit]">
          {isPlayerFinishedPlaying && (
            <ArrowPathIcon
              className="inline-block w-6 h-6 mr-1 text-white cursor-pointer opacity-70 hover:opacity-100"
              onClick={(e) => {
                (setIsPlayerFinishedPlaying(false),
                  e.preventDefault(),
                  e.stopPropagation());
              }}
            />
          )}
          {isPlayerMuted ? (
            <SpeakerXMarkIcon
              className="inline-block w-6 h-6 mr-1 text-white cursor-pointer opacity-70 hover:opacity-100"
              onClick={(e) => {
                (storeMutedState(false),
                  e.preventDefault(),
                  e.stopPropagation());
              }}
            />
          ) : (
            <SpeakerWaveIcon
              className="inline-block w-6 h-6 mr-1 text-white cursor-pointer opacity-70 hover:opacity-100"
              onClick={(e) => {
                (storeMutedState(true),
                  e.preventDefault(),
                  e.stopPropagation());
              }}
            />
          )}
        </div>
      )}
    </>
  );
};
